import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, addDoc, onSnapshot, deleteDoc, doc, serverTimestamp, updateDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { ayKey, AY_ADLARI, paraCevir } from '../utils/takvim';
import { Plus, Trash2, MapPin, Star, Check, ChevronLeft, ChevronRight, Calculator, Wallet } from 'lucide-react';

// AdminLayout'tan gelen onSantiyeSelect prop'unu buraya ekledik
export default function Santiyeler({ onSantiyeSelect }) {
  const navigate = useNavigate();
  const bugun = new Date();
  const [yil, setYil] = useState(bugun.getFullYear());
  const [ay, setAy] = useState(bugun.getMonth());

  const [santiyeler, setSantiyeler] = useState([]);
  const [ustalar, setUstalar] = useState([]);
  const [calisma, setCalisma] = useState({});
  const [yeniAd, setYeniAd] = useState('');
  const [silOnay, setSilOnay] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);

  const ak = ayKey(yil, ay);

  useEffect(() => {
    const unsubSantiye = onSnapshot(collection(db, 'santiyeler'), snap => {
      const liste = snap.docs.map(d => ({
        id: d.id, ad: '', aktif: true, favori: false, ...d.data()
      }));
      liste.sort((a, b) => {
        if (a.favori && !b.favori) return -1;
        if (!a.favori && b.favori) return 1;
        return (a.ad || '').localeCompare(b.ad || '', 'tr');
      });
      setSantiyeler(liste);
    });

    const unsubUstalar = onSnapshot(collection(db, 'ustalar'), snap => {
      setUstalar(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubCalisma = onSnapshot(doc(db, 'aylikCalisma', ak), snap => {
      setCalisma(snap.exists() ? snap.data().kayitlar : {});
    });

    return () => { unsubSantiye(); unsubUstalar(); unsubCalisma(); };
  }, [ak]);

  const getSantiyeAnaliz = (sId) => {
    let toplamGun = 0;
    let toplamMaliyet = 0;
    Object.values(calisma).forEach(ustaMap => {
      Object.entries(ustaMap).forEach(([uId, santiyeId]) => {
        if (santiyeId === sId) {
          toplamGun++;
          const usta = ustalar.find(u => u.id === uId);
          toplamMaliyet += Number(usta?.yovmiye || 0);
        }
      });
    });
    return { toplamGun, toplamMaliyet };
  };

  function ayDegistir(delta) {
    let yeniAy = ay + delta;
    let yeniYil = yil;
    if (yeniAy > 11) { yeniAy = 0; yeniYil++; }
    if (yeniAy < 0) { yeniAy = 11; yeniYil--; }
    setAy(yeniAy); setYil(yeniYil);
  }

  async function ekle(e) {
    e.preventDefault();
    if (!yeniAd.trim()) return;
    setYukleniyor(true);
    try {
      await addDoc(collection(db, 'santiyeler'), {
        ad: yeniAd.trim(),
        aktif: true,
        favori: false,
        olusturmaTarihi: serverTimestamp(),
        konum: '',
        telefon: '',
        notlar: ''
      });
      setYeniAd('');
    } catch (err) { alert('Hata: ' + err.message); }
    finally { setYukleniyor(false); }
  }

  const toggle = async (e, id, field, current) => {
    e.stopPropagation();
    await updateDoc(doc(db, 'santiyeler', id), { [field]: !current });
  };

  const silIslemi = async (e, id) => {
    e.stopPropagation();
    await deleteDoc(doc(db, 'santiyeler', id));
    setSilOnay(null);
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-6 pb-24">
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between border-b border-stone-800 pb-6">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <MapPin size={20} className="text-stone-400" /> Şantiyeler
          </h1>
          <p className="text-[10px] text-stone-500 uppercase font-black tracking-widest mt-1">Aylık Hakediş ve Mesai Takibi</p>
        </div>

        <div className="flex items-center bg-stone-900 border border-stone-800 rounded-xl p-1">
          <button onClick={() => ayDegistir(-1)} className="p-2 text-stone-400 hover:bg-stone-800 rounded-lg transition-colors"><ChevronLeft size={18} /></button>
          <span className="text-xs font-black text-white px-4 min-w-[120px] text-center uppercase">{AY_ADLARI[ay]} {yil}</span>
          <button onClick={() => ayDegistir(1)} className="p-2 text-stone-400 hover:bg-stone-800 rounded-lg transition-colors"><ChevronRight size={18} /></button>
        </div>
      </div>

      <form onSubmit={ekle} className="flex gap-2">
        <input value={yeniAd} onChange={e => setYeniAd(e.target.value)}
          placeholder="Yeni şantiye adı..."
          className="flex-1 bg-stone-900 border border-stone-800 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-stone-600 placeholder-stone-700" />
        <button type="submit" disabled={yukleniyor || !yeniAd.trim()}
          className="bg-white text-stone-900 font-bold px-6 py-3 rounded-xl text-sm hover:bg-stone-200 disabled:opacity-40 flex items-center gap-2">
          <Plus size={16} /> Ekle
        </button>
      </form>

      <div className="space-y-3">
        {santiyeler.map(s => {
          const analiz = getSantiyeAnaliz(s.id);

          return (
            <div
              key={s.id}
              onClick={() => {
                if (typeof onSantiyeSelect === 'function') {
                  onSantiyeSelect(s.id);
                }
                // BAŞINA /admin/ EKLEDİK VE 'santiyeler' YAPTIK
                navigate(`/admin/santiyeler/${s.id}`);
              }}
              className={`bg-stone-900 border rounded-2xl p-4 transition-all cursor-pointer hover:border-stone-600 active:scale-[0.98] ${s.aktif ? 'border-stone-800' : 'border-stone-800/30 opacity-50'
                }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${s.aktif ? 'bg-stone-800 text-stone-300' : 'bg-stone-950 text-stone-700'}`}>
                    <MapPin size={16} />
                  </div>
                  <div>
                    <h3 className={`text-sm font-bold ${s.aktif ? 'text-white' : 'text-stone-600'}`}>{s.ad}</h3>
                    {s.favori && <span className="text-[10px] text-yellow-500 font-black uppercase tracking-tighter">Favori Şantiye</span>}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button onClick={(e) => toggle(e, s.id, 'aktif', s.aktif)} className={`p-2 rounded-lg transition-colors ${s.aktif ? 'text-green-500 hover:bg-green-500/10' : 'text-stone-700'}`}>
                    {s.aktif ? <Check size={18} /> : <div className="w-4 h-4 border-2 border-stone-800 rounded-full" />}
                  </button>
                  <button onClick={(e) => toggle(e, s.id, 'favori', s.favori)} className={`p-2 rounded-lg transition-colors ${s.favori ? 'text-yellow-500' : 'text-stone-700 hover:text-yellow-500'}`}>
                    <Star size={18} className={s.favori ? 'fill-yellow-500' : ''} />
                  </button>
                  {silOnay === s.id ? (
                    <div className="flex gap-1 ml-2" onClick={e => e.stopPropagation()}>
                      <button onClick={(e) => silIslemi(e, s.id)} className="bg-red-500 text-white text-[10px] px-2 py-1 rounded-lg font-bold">SİL</button>
                      <button onClick={(e) => { e.stopPropagation(); setSilOnay(null); }} className="text-stone-500 text-[10px] px-2 py-1">İPTAL</button>
                    </div>
                  ) : (
                    <button onClick={(e) => { e.stopPropagation(); setSilOnay(s.id); }} className="p-2 text-stone-800 hover:text-red-500 transition-colors">
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>

              {analiz.toplamGun > 0 ? (
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-stone-800/50">
                  <div className="bg-stone-950/50 rounded-xl p-3 border border-stone-800/30">
                    <div className="flex items-center gap-2 text-stone-500 mb-1">
                      <Calculator size={12} />
                      <span className="text-[10px] font-black uppercase tracking-widest">Aylık Mesai</span>
                    </div>
                    <div className="text-stone-200 font-bold">{analiz.toplamGun} Gün</div>
                  </div>
                  <div className="bg-stone-950/50 rounded-xl p-3 border border-stone-800/30">
                    <div className="flex items-center gap-2 text-stone-500 mb-1">
                      <Wallet size={12} className="text-green-600" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Yevmiye Toplamı</span>
                    </div>
                    <div className="text-green-400 font-bold">{paraCevir(analiz.toplamMaliyet)}</div>
                  </div>
                </div>
              ) : (
                <div className="text-[10px] text-stone-700 italic px-1">Bu ay bu şantiyede çalışma kaydı bulunmuyor.</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}